/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  MenuItem,
  Grid,
  TablePagination,
  InputAdornment,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Button,
  Autocomplete,
  CircularProgress,
  Backdrop,
  ListSubheader,
  Collapse,
  useMediaQuery,
  useTheme,
  CardActionArea,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { executionApi, aiSystemApi } from '../../services/api';
import { formatStoredDateTime } from '../../utils/datetime';
import { PageHeader, LoadingSpinner, ErrorAlert } from '../common';
import ExecutionViewerDialog from './ExecutionViewerDialog';
import { formatCredits, getExecutionCreditUsage } from './runsPresentation';

interface ExecutionListProps {
  embedded?: boolean;
}

const ExecutionList: React.FC<ExecutionListProps> = ({ embedded = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Filter states
  const [aiSystem, setAiSystem] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [executionType, setExecutionType] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [hasActions, setHasActions] = useState<string>('');
  const [actionStatus, setActionStatus] = useState<string>('');
  const [actionType, setActionType] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [filtersExpanded, setFiltersExpanded] = useState<boolean>(!isMobile);
  
  // Execution viewer state
  const [selectedExecution, setSelectedExecution] = useState<any>(null);
  const [viewerOpen, setViewerOpen] = useState<boolean>(false);
  const [loadingDetail, setLoadingDetail] = useState<boolean>(false);

  // Fetch AI Systems for filter
  const { data: aiSystemsData } = useQuery({
    queryKey: ['ai-systems-list'],
    queryFn: () => aiSystemApi.list({ page_size: 1000 }),
  });

  // Fetch available action types and tool names for filter
  const { data: actionTypesData } = useQuery({
    queryKey: ['execution-action-types'],
    queryFn: () => executionApi.actionTypes(),
    staleTime: 60000, // Cache for 1 minute
  });

  // Build query params - only add filters if they have values
  const queryParams: Record<string, any> = {
    page: page + 1, // API is 1-indexed
    page_size: pageSize,
  };
  
  // Only add non-empty filter values
  if (aiSystem && aiSystem !== '') {
    queryParams.ai_system = aiSystem;
  }
  if (status && status !== '') {
    queryParams.status = status;
  }
  if (executionType && executionType !== '') {
    queryParams.execution_type = executionType;
  }
  if (search && search.trim() !== '') {
    queryParams.search = search.trim();
  }
  if (hasActions) {
    queryParams.has_actions = hasActions;
  }
  if (actionStatus) {
    queryParams.action_status = actionStatus;
  }
  if (actionType) {
    queryParams.action_type = actionType;
  }
  if (dateFrom) {
    queryParams.created_after = new Date(dateFrom).toISOString();
  }
  if (dateTo) {
    queryParams.created_before = new Date(dateTo).toISOString();
  }

  // Fetch executions
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['executions', queryParams],
    queryFn: () => executionApi.list(queryParams),
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['execution-stats'],
    queryFn: () => executionApi.stats(),
  });

  if (error) {
    return <ErrorAlert error={error as Error} />;
  }

  const getStatusColor = (status: string): "default" | "success" | "error" | "warning" | "info" => {
    switch (status.toUpperCase()) {
      case 'COMPLETED':
        return 'success';
      case 'ERROR':
        return 'error';
      case 'RUNNING':
        return 'info';
      case 'START':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getActionsSummary = (execution: any): { total: number; successful: number; failed: number } | null => {
    // Use pre-computed actions_summary from backend (much faster than computing from expert_results)
    const summary = execution.actions_summary;
    if (!summary || summary.total === 0) {
      return null;
    }
    return { 
      total: summary.total, 
      successful: summary.successful, 
      failed: summary.failed 
    };
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleViewExecution = async (execution: any) => {
    // Fetch full execution details before opening dialog
    setLoadingDetail(true);
    try {
      const fullExecution = await executionApi.get(execution.id);
      setSelectedExecution(fullExecution);
      setViewerOpen(true);
    } catch (error) {
      console.error('Failed to fetch execution details:', error);
      // Fallback to list data if fetch fails (though it may have missing fields)
      setSelectedExecution(execution);
      setViewerOpen(true);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
    setSelectedExecution(null);
  };

  const headerActions = (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      {isMobile && (
        <Tooltip title={filtersExpanded ? 'Hide Filters' : 'Show Filters'}>
          <IconButton onClick={() => setFiltersExpanded(!filtersExpanded)} color="primary" size="small">
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Refresh">
        <IconButton onClick={handleRefresh} color="primary" size="small">
          <RefreshIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <Box>
      {embedded ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          {headerActions}
        </Box>
      ) : (
        <PageHeader title="Runs" actions={headerActions} />
      )}

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Typography color="textSecondary" gutterBottom variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                  Total Runs
                </Typography>
                <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                  {stats.total_executions}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Typography color="textSecondary" gutterBottom variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                  Success Rate
                </Typography>
                <Typography variant="h5" color="success.main" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                  {stats.success_rate}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Typography color="textSecondary" gutterBottom variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                  Last 24 Hours
                </Typography>
                <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                  {stats.recent?.last_24h || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Typography color="textSecondary" gutterBottom variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                  Last 7 Days
                </Typography>
                <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                  {stats.recent?.last_7d || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters - Collapsible on mobile */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
        {isMobile && (
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: filtersExpanded ? 2 : 0,
              cursor: 'pointer'
            }}
            onClick={() => setFiltersExpanded(!filtersExpanded)}
          >
            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FilterListIcon fontSize="small" /> Filters
            </Typography>
            {filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>
        )}
        
        <Collapse in={!isMobile || filtersExpanded}>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {/* Row 1: Search, AI System, Status */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="Search"
                placeholder="Search..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Autocomplete
                options={aiSystemsData?.results || []}
                getOptionLabel={(option: any) => option.name || ''}
                value={aiSystemsData?.results?.find((sys: any) => sys.id.toString() === aiSystem) || null}
                onChange={(_event, newValue) => {
                  setAiSystem(newValue ? newValue.id.toString() : '');
                  setPage(0);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="AI System"
                    placeholder="All"
                    size="small"
                  />
                )}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                select
                label="Status"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(0);
                }}
                size="small"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="start">Start</MenuItem>
                <MenuItem value="running">Running</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="error">Error</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                select
                label="Type"
                value={executionType}
                onChange={(e) => {
                  setExecutionType(e.target.value);
                  setPage(0);
                }}
                size="small"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="manual">Manual</MenuItem>
                <MenuItem value="scheduled">Scheduled</MenuItem>
                <MenuItem value="api">API</MenuItem>
                <MenuItem value="backtest">Backtest</MenuItem>
              </TextField>
            </Grid>

            {/* Row 2: Action Filters */}
            <Grid size={{ xs: 6, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                select
                label="Has Actions"
                value={hasActions}
                onChange={(e) => {
                  setHasActions(e.target.value);
                  setPage(0);
                }}
                size="small"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                select
                label="Action Status"
                value={actionStatus}
                onChange={(e) => {
                  setActionStatus(e.target.value);
                  setPage(0);
                }}
                size="small"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="mixed">Mixed</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                select
                label="Action/Tool"
                value={actionType}
                onChange={(e) => {
                  setActionType(e.target.value);
                  setPage(0);
                }}
                size="small"
              >
                <MenuItem value="">All Types</MenuItem>
                {/* Dynamic action types from API */}
                {actionTypesData?.action_types && actionTypesData.action_types.length > 0 && [
                  <ListSubheader key="actions-header">Actions</ListSubheader>,
                  ...actionTypesData.action_types.map((type: string) => (
                    <MenuItem key={type} value={type}>
                      {type.replace(/_/g, ' ')}
                    </MenuItem>
                  ))
                ]}
                {/* Dynamic tool names from API */}
                {actionTypesData?.tool_names && actionTypesData.tool_names.length > 0 && [
                  <ListSubheader key="tools-header">Tool Calls</ListSubheader>,
                  ...actionTypesData.tool_names.map((toolName: string) => (
                    <MenuItem key={`tool:${toolName}`} value={`tool:${toolName}`}>
                      🔧 {toolName}
                    </MenuItem>
                  ))
                ]}
              </TextField>
            </Grid>

            {/* Row 3: Date Range */}
            <Grid size={{ xs: 6, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Date From"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(0);
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Date To"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(0);
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                size="small"
              />
            </Grid>

            {/* Clear Filters Button */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={() => {
                  setAiSystem('');
                  setStatus('');
                  setExecutionType('');
                  setSearch('');
                  setHasActions('');
                  setActionStatus('');
                  setActionType('');
                  setDateFrom('');
                  setDateTo('');
                  setPage(0);
                }}
                size="small"
                sx={{ height: '40px' }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </Collapse>
      </Paper>

      {/* Executions Table / Card List */}
      {isLoading ? (
        <LoadingSpinner message="Loading executions..." />
      ) : isMobile ? (
        /* Mobile Card View */
        <Box>
          <Grid container spacing={1.5}>
            {data?.results?.map((execution: any) => {
              const actionsSummary = getActionsSummary(execution);
              const creditUsage = getExecutionCreditUsage(execution);
              
              return (
                <Grid size={{ xs: 12 }} key={execution.id}>
                  <Card>
                    <CardActionArea onClick={() => handleViewExecution(execution)}>
                      <CardContent sx={{ p: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                              {execution.system_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                              {execution.id.substring(0, 8)}...
                            </Typography>
                          </Box>
                          <Chip
                            label={execution.status}
                            size="small"
                            color={getStatusColor(execution.status)}
                            sx={{ fontSize: '0.65rem', height: 20 }}
                          />
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                          <Chip
                            label={execution.execution_type}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.6rem', height: 18 }}
                          />
                          {actionsSummary && (
                            <>
                              <Chip
                                label={`${actionsSummary.total} actions`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.6rem', height: 18 }}
                              />
                              {actionsSummary.successful > 0 && (
                                <Chip
                                  label={`✓ ${actionsSummary.successful}`}
                                  size="small"
                                  color="success"
                                  sx={{ fontSize: '0.6rem', height: 18 }}
                                />
                              )}
                              {actionsSummary.failed > 0 && (
                                <Chip
                                  label={`✗ ${actionsSummary.failed}`}
                                  size="small"
                                  color="error"
                                  sx={{ fontSize: '0.6rem', height: 18 }}
                                />
                              )}
                            </>
                          )}
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                            {formatStoredDateTime(execution.created_at, undefined, '-')}
                            {execution.execution_time_ms != null && (
                              <> · {execution.execution_time_ms >= 1000 ? `${(execution.execution_time_ms / 1000).toFixed(1)}s` : `${execution.execution_time_ms.toFixed(0)}ms`}</>
                            )}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            {creditUsage !== null && (
                              <Typography variant="caption" fontWeight="medium" sx={{ fontSize: '0.65rem' }}>
                                {formatCredits(creditUsage, { compact: true })}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          
          {(!data?.results || data.results.length === 0) && (
            <Card sx={{ mt: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body2" color="textSecondary">
                  No executions found. Try adjusting your filters.
                </Typography>
              </CardContent>
            </Card>
          )}
          
          <TablePagination
            component="div"
            count={data?.count || 0}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={pageSize}
            onRowsPerPageChange={handlePageSizeChange}
            rowsPerPageOptions={[10, 20, 50]}
            sx={{ 
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontSize: '0.75rem'
              }
            }}
          />
        </Box>
      ) : (
        /* Desktop Table View */
        <Paper>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>System</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions/Tools</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Credit Usage</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.results?.map((execution: any) => {
                  const actionsSummary = getActionsSummary(execution);
                  
                  return (
                    <TableRow 
                      key={execution.id} 
                      hover
                      onClick={() => handleViewExecution(execution)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {execution.id.substring(0, 8)}...
                        </Typography>
                      </TableCell>
                      <TableCell>{execution.system_name}</TableCell>
                      <TableCell>
                        <Chip
                          label={execution.execution_type}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={execution.status}
                          size="small"
                          color={getStatusColor(execution.status)}
                        />
                      </TableCell>
                      <TableCell>
                        {actionsSummary ? (
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <Chip
                              label={`${actionsSummary.total}`}
                              size="small"
                              variant="outlined"
                              sx={{ minWidth: 35 }}
                            />
                            {actionsSummary.successful > 0 && (
                              <Chip
                                label={`✓ ${actionsSummary.successful}`}
                                size="small"
                                color="success"
                                variant="filled"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                            {actionsSummary.failed > 0 && (
                              <Chip
                                label={`✗ ${actionsSummary.failed}`}
                                size="small"
                                color="error"
                                variant="filled"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatStoredDateTime(execution.created_at, undefined, '-')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {execution.execution_time_ms != null
                            ? execution.execution_time_ms >= 60000
                              ? `${(execution.execution_time_ms / 60000).toFixed(1)}m`
                              : execution.execution_time_ms >= 1000
                              ? `${(execution.execution_time_ms / 1000).toFixed(2)}s`
                              : `${execution.execution_time_ms.toFixed(0)}ms`
                            : execution.completed_at
                            ? `${((new Date(execution.completed_at).getTime() - new Date(execution.created_at).getTime()) / 1000).toFixed(1)}s`
                            : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium" color="primary">
                          {formatCredits(getExecutionCreditUsage(execution), { compact: true })}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {(!data?.results || data.results.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                        No executions found. Try adjusting your filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={data?.count || 0}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={pageSize}
            onRowsPerPageChange={handlePageSizeChange}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </Paper>
      )}

      {/* Loading Backdrop while fetching execution details */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loadingDetail}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Execution Viewer Dialog */}
      {selectedExecution && (
        <ExecutionViewerDialog
          open={viewerOpen}
          onClose={handleCloseViewer}
          execution={selectedExecution}
        />
      )}
    </Box>
  );
};

export default ExecutionList;
